import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * SEOHead component for managing page-specific SEO meta tags
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.keywords - Comma-separated keywords
 * @param {string} props.canonicalPath - Path for canonical URL (without domain)
 * @param {string} props.ogImage - Custom Open Graph image path (optional)
 * @param {string} props.ogType - Open Graph type (default: website)
 * @param {string} props.domain - Domain for canonical URLs (optional)
 */
const SEOHead = ({
  title = 'FileZen | AI-Powered Document Enhancer',
  description = 'Transform your documents with AI-powered enhancement, intelligent data analysis, and content optimization',
  keywords = 'FileZen, AI document processing, document enhancer, PDF enhancement, data analysis, content optimization',
  canonicalPath = '',
  ogImage = '/images/filezen-social-preview.svg',
  ogType = 'website',
  domain = 'https://filezen.smartbusinessai.cloud'
}) => {
  const fullTitle = title.includes('FileZen') ? title : `${title} | FileZen`;
  const canonicalUrl = `${domain}${canonicalPath}`;
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${domain}${ogImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
    </Helmet>
  );
};

export default SEOHead; 